import * as React from "react";
import { useEffect } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import { triggerOktaLogin } from "../../utils/oktaUtil";
import { navigateTo } from "../../../navigateTo";
import {IRIS_ORIGINAL_URI_KEY} from "../root/SecureRoute";

const AonLogo: React.FunctionComponent = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 907 344" className="landing-page__aon-logo">
        <g fill="#eb0017" transform="translate(-177.14 42.3)">
            <path d="m890.73 117.56c0 2.64-.05 5.28-.05 7.91v168.53h-63.77v-288c.77 0 1.54-.1 2.3-.1h51.12c1.434-.112 2.828.533 3.67 1.7 44.827 55.653 89.683 111.29 134.57 166.91.44.55.92 1.06 1.82 2.08v-170.53h63.77v288.1h-48c-1.34.101-2.63-.507-3.41-1.6-33.833-41.94-67.67-83.877-101.51-125.81-13.3-16.48-26.633-32.947-40-49.4l-.49-.45z"/>
            <path d="m643.3 301.39c-34.74-1-70.82-15.39-100.1-45.11-29.53-30-44.33-66.38-43.73-108.56.59-40.87 15.76-75.93 44.58-104.76 25.95-25.96 57.45-40.72 94.09-43.44 47.29-3.5 88 11.69 121.1 45.65 28.46 29.17 42.53 64.71 42.46 105.36-.13 82.71-66.89 150.47-158.4 150.86zm94.3-144.39c0-31.09-8.57-51.55-25.28-68.43s-37.54-25.51-61.4-25.69-44.7 8.36-61.58 25.26c-19.78 19.79-28.49 44-25.47 71.86 1.938 19.264 10.551 37.258 24.34 50.85 19.51 19.57 43.17 28.62 70.82 26.1 41.48-3.78 79.24-39.61 78.57-79.95z"/>
            <path d="m523.14 294.16h-67.41c-1.393.212-2.743-.648-3.14-2-17-40.96-34.04-81.9-51.12-122.82-4.37-10.44-8.87-20.86-13.27-31.34-4.067-9.66-8.107-19.327-12.12-29-8.34-20.187-16.673-40.38-25-60.58-.27-.64-.64-1.24-1.16-2.24-18.21 43.82-36.26 87.14-54.47 130.82h70.83c1.485-.209 2.91.725 3.31 2.17 7.94 18.28 15.94 36.543 24 54.79.31.71.57 1.44 1 2.53h-3.05c-39.333 0-78.693-.023-118.08-.07-2.3 0-3.14.84-3.92 2.72-7.31 17.69-14.7 35.34-22 53-.53 1.28-1 2.06-2.64 2.06-22.24-.067-44.48-.067-66.72 0-.24 0-.48 0-1-.09.189-.678.412-1.345.67-2 22.827-52.6 45.66-105.197 68.5-157.79 25.18-58 50.337-116.027 75.47-174.08.81-1.88 1.72-2.56 3.82-2.54 16.32.1 32.653.1 49 0 2.14 0 3 .73 3.81 2.58 22.967 53.06 45.967 106.107 69 159.14 24.887 57.333 49.77 114.693 74.65 172.08.28.79.6 1.58 1.04 2.66z"/>
        </g>
    </svg>
);

export const LandingPage: React.FunctionComponent = () => {
    const { authState } = useOktaAuth();
    const navigate: NavigateFunction = useNavigate();

    useEffect(() => {
        if (authState?.isAuthenticated) {
            navigateTo("/home", navigate);
        }
    }, [authState?.isAuthenticated, navigate]);

    const handleSignIn = () => {
        const storedUri = localStorage.getItem(IRIS_ORIGINAL_URI_KEY);
        
        let originalUri = storedUri || "/home";
        if (originalUri === "/landing" || originalUri === "/" || originalUri === "/login/callback") {
            originalUri = "/home";
        }
        
        triggerOktaLogin(originalUri).catch((err) => 
            console.error("Sign-in error:", err)
        );
    };

    if (authState?.isAuthenticated) {
        return null;
    }

    return (
        <div className="landing-page">
            <div className="landing-page__left-panel">
                <div className="landing-page__logo-header">
                    <span className="landing-page__logo-text">AON</span>
                    <span className="landing-page__logo-divider">|</span>
                    <span className="landing-page__logo-iris">Iris</span>
                </div>
            </div>

            <div className="landing-page__right-panel">
                <div className="landing-page__sign-in-card">
                    <h1 className="landing-page__welcome-title">Welcome Back</h1>
                    <p className="landing-page__welcome-subtitle">Sign in to access your investment insights</p>

                    <div className="landing-page__sign-in-box">
                        <AonLogo />
                        <h2 className="landing-page__sign-in-title">Sign In</h2>
                        <p className="landing-page__sign-in-description">
                            Click below to securely sign in with your Aon credentials
                        </p>

                        <button 
                            className="landing-page__sign-in-button"
                            onClick={handleSignIn}
                            data-testid="landing-sign-in-button"
                        >
                            <i className="fa-solid fa-arrow-right-to-bracket" aria-hidden="true" />
                            Continue to Sign In
                        </button>

                        <div className="landing-page__support-link">
                            Need help?{" "}
                            {/* eslint-disable-next-line react/forbid-dom-props */}
                            <a href="mailto:support@aoniris.com">Contact Support</a>
                        </div>
                    </div>

                    <div className="landing-page__secure-notice">
                        <h3 className="landing-page__secure-notice-title">Secure Authentication</h3>
                        <p className="landing-page__secure-notice-text">
                            You'll be redirected to Aon's secure login page to enter your credentials,
                            then returned to Iris.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};